import { useI18n } from '@/shared/i18n/useT';
import { useBookingT, type BookingMessageKey, type BookingTranslate } from './i18n';
import type { LocationType, PublicEventType } from './types';

const LOCATION_KEYS: Record<LocationType, BookingMessageKey> = {
  custom: 'publicBooking.location.custom',
  in_person: 'publicBooking.location.in_person',
  phone: 'publicBooking.location.phone',
  video: 'publicBooking.location.video',
  google_meet: 'publicBooking.location.video',
  zoom: 'publicBooking.location.video',
  teams: 'publicBooking.location.video',
};

export function locationLabel(
  t: BookingTranslate,
  type: LocationType | null,
  value: string | null,
): string {
  const label = type ? t(LOCATION_KEYS[type]) : t('publicBooking.location.custom');
  return value ? `${label} · ${value}` : label;
}

interface Props {
  event: PublicEventType;
  professionalTitle: string;
  /** Rendered under the summary — the chosen slot, on later steps. */
  when?: string;
  timeZone?: string;
}

/** The left pane: what you are booking, before anything is committed. */
export function EventSummary({
  event,
  professionalTitle,
  when,
  timeZone,
}: Props) {
  const t = useBookingT();
  const { formatCurrency } = useI18n();

  const price =
    event.price === null || event.price === 0
      ? t('publicBooking.free')
      : formatCurrency(event.price, event.currency);

  return (
    <div className="rounded-token border border-border bg-surface-2 p-4 sm:p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">
        {t('publicBooking.hostedBy')}
      </p>
      <p className="mt-1 text-sm font-medium text-fg">{professionalTitle}</p>

      <h2 className="mt-3 text-lg font-semibold text-fg">{event.name}</h2>
      {event.description && (
        <p className="mt-2 text-sm text-muted">{event.description}</p>
      )}

      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex gap-2">
          <dt className="sr-only">{t('calendarUi.dateTime')}</dt>
          <dd className="text-muted">
            {`${event.durationMin} ${t('calendarUi.minutes')}`}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="sr-only">{t('publicBooking.free')}</dt>
          <dd className="text-muted">{price}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="sr-only">{t('publicBooking.location.label')}</dt>
          <dd className="text-muted">
            {locationLabel(t, event.locationType, event.locationValue)}
          </dd>
        </div>
        {when && (
          <div className="flex gap-2">
            <dt className="sr-only">{t('calendarUi.dateTime')}</dt>
            <dd className="font-medium text-accent">{when}</dd>
          </div>
        )}
        {timeZone && (
          <div className="flex gap-2">
            <dt className="sr-only">{t('publicBooking.timezone.label')}</dt>
            <dd className="text-xs text-muted">
              {t('publicBooking.timezone.shownIn', { timezone: timeZone })}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
